import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { useAddressAutocompleteActions } from "@redotech/redo-merchant-app-common/hooks/use-address-autocomplete-actions";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import {
  AddressAutocompleteForm,
  createAddressAutocompleteForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useMemo } from "react";
import { getCustomAddressesQueryKey } from "../shipping/hooks/use-custom-addresses";

interface AddLocationModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSave: (location: Step.LocationWithAddress) => void;
  initialLocation?: Step.LocationWithAddress | null;
}

export const AddLocationModal = memo(function AddLocationModal({
  open,
  setOpen,
  onSave,
  initialLocation,
}: AddLocationModalProps) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();

  const addressForm = useInput(
    useMemo(() => createAddressAutocompleteForm(false, true), []),
    {
      name: "",
      address1: "",
      address2: null,
      city: "",
      company: null,
      province: null,
      zip: "",
      countryCode: RedoShopifyCountryCode.Us,
      phone: null,
      email: null,
    },
  );

  const actions = useAddressAutocompleteActions();

  useEffect(() => {
    if (initialLocation) {
      addressForm.setValue({
        name: initialLocation.name,
        address1: initialLocation.address.street1,
        address2: initialLocation.address.street2 || null,
        city: initialLocation.address.city,
        company: null,
        province: initialLocation.address.state || null,
        zip: initialLocation.address.zip,
        countryCode: initialLocation.address.country as RedoShopifyCountryCode,
        phone: initialLocation.address.phone || null,
        email: null,
      });
    } else {
      addressForm.setValue({
        name: "",
        address1: "",
        address2: null,
        city: "",
        company: null,
        province: null,
        zip: "",
        countryCode: RedoShopifyCountryCode.Us,
        phone: null,
        email: null,
      });
    }
    // todo resolve issue with unable to fill out forms without needing this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation, open]);

  const handleSave = async () => {
    let locationId: string | undefined = initialLocation?.locationId;
    const externalLocationId: string | undefined =
      initialLocation?.externalLocationId;

    const formValue = addressForm.value;

    if (!initialLocation) {
      try {
        const response = await rpcClient.createAddress({
          address: {
            name: formValue.name,
            street1: formValue.address1,
            street2: formValue.address2 || undefined,
            city: formValue.city,
            state: formValue.province || undefined,
            zip: formValue.zip,
            country: formValue.countryCode,
            phone: formValue.phone || undefined,
          },
        });
        locationId = response.addressId;

        await queryClient.invalidateQueries({
          queryKey: getCustomAddressesQueryKey(),
        });
      } catch (error) {
        console.error("Failed to save custom address:", error);
      }
    }

    const location: Step.LocationWithAddress = {
      name: formValue.name,
      address: {
        street1: formValue.address1,
        street2: formValue.address2 || undefined,
        city: formValue.city,
        state: formValue.province || "",
        zip: formValue.zip,
        country: formValue.countryCode,
        phone: formValue.phone || undefined,
      },
      locationId,
      externalLocationId,
    };

    onSave(location);
    setOpen(false);
  };

  const isValid = addressForm.allErrors.length === 0;

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => setOpen(false)}
      primaryButton={{
        size: "sm",
        text: initialLocation ? t("Update location") : t("Add location"),
        onClick: handleSave,
        disabled: !isValid,
      }}
      secondaryButton={{
        text: t("Cancel"),
        onClick: () => setOpen(false),
        size: "sm",
      }}
      title={
        initialLocation ? t("Edit custom location") : t("Add custom location")
      }
    >
      <Flex dir="column" gap="md" w="full">
        <AddressAutocompleteForm
          actions={actions}
          autoCompleteOnName={false}
          form={addressForm}
          gap="md"
          hideCompanyField
          hideEmailField
          includeNameField
          requirePhone
          title=""
        />
      </Flex>
    </RedoModal>
  );
});
